/**
 * Calculate the expected .currentTime of a video/audio element
 * given the current frame, playback rate, and offset.
 *
 * Written from scratch for Steve.AI.
 */

import { interpolate } from '../interpolate.js';

/**
 * Get the raw expected frame position (before fps conversion).
 */
export const getExpectedMediaFrameUncorrected = ({ frame, playbackRate, startFrom }) => {
  return interpolate(
    frame,
    [-1, startFrom, startFrom + 1],
    [-1, startFrom, startFrom + playbackRate]
  );
};

/**
 * Convert a frame number to the expected currentTime in seconds,
 * accounting for playback rate and browser quirks.
 *
 * @param {object} params
 * @param {number} params.fps
 * @param {number} params.frame
 * @param {string} params.src
 * @param {number} params.playbackRate
 * @param {number} params.startFrom
 * @param {string} params.mediaType - 'video' or 'audio'
 * @returns {number} Expected currentTime in seconds
 */
export const getMediaTime = ({ fps, frame, src, playbackRate, startFrom, mediaType }) => {
  const expectedFrame = getExpectedMediaFrameUncorrected({ frame, playbackRate, startFrom });

  // Chrome <112 has a quirk with MP4 where first frame displays at 1/fps
  const isChrome =
    typeof window !== 'undefined' &&
    window.navigator.userAgent.match(/Chrome\/([0-9]+)/);

  if (
    isChrome &&
    Number(isChrome[1]) < 112 &&
    mediaType === 'video' &&
    src.endsWith('.mp4')
  ) {
    return (expectedFrame + 1) / fps;
  }

  // For other formats, add half-frame offset for accurate seeking
  const msPerFrame = 1000 / fps;
  const msShift = msPerFrame / 2;
  return (expectedFrame * msPerFrame + msShift) / 1000;
};
